import React, { useState } from 'react';
import { memberPlans } from '../data/mock';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check } from 'lucide-react';
import QuickPayButton from './QuickPayButton';
import GymContactModal from './GymContactModal';

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('members');
  const [isGymModalOpen, setIsGymModalOpen] = useState(false);

  const scrollToBooking = () => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="bg-[#dfddd6] py-20 md:py-32">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-['Bebas_Neue'] text-[#0d0d0d] text-5xl md:text-7xl tracking-wide mb-8">
            PLANS FOR EVERY MEMBER
          </h2>

          {/* Toggle Tabs */}
          <div className="inline-flex bg-[#302f2c] rounded-full p-1 mb-16">
            <button
              onClick={() => setActiveTab('members')}
              className={`font-['DM_Sans'] font-semibold px-8 py-3 rounded-full transition-all ${
                activeTab === 'members'
                  ? 'bg-[#e8ff47] text-[#0d0d0d]'
                  : 'text-[#dfddd6] hover:text-[#e8ff47]'
              }`}
            >
              For Members
            </button>
            <button
              onClick={() => setActiveTab('gyms')}
              className={`font-['DM_Sans'] font-semibold px-8 py-3 rounded-full transition-all ${
                activeTab === 'gyms'
                  ? 'bg-[#e8ff47] text-[#0d0d0d]'
                  : 'text-[#dfddd6] hover:text-[#e8ff47]'
              }`}
            >
              For Gyms
            </button>
          </div>
        </div>

        {/* Member Plans */}
        {activeTab === 'members' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {memberPlans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 hover:scale-105 ${
                  plan.premium
                    ? 'bg-[#0d0d0d] border-[#e8ff47] border-2'
                    : 'bg-white border-[#888680] border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#e8ff47] text-[#0d0d0d] font-['DM_Sans'] font-bold uppercase">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <h3 className={`font-['Bebas_Neue'] text-3xl mb-2 ${
                    plan.premium ? 'text-[#e8ff47]' : 'text-[#0d0d0d]'
                  }`}>
                    {plan.name}
                  </h3>

                  {/* Discount Badge */}
                  {plan.discount && (
                    <div className="mb-2">
                      <Badge className="bg-green-500 text-white font-['DM_Sans'] font-bold text-xs px-2 py-1">
                        {plan.discount}
                      </Badge>
                    </div>
                  )}

                  {/* Original Price with Strikethrough */}
                  {plan.originalPrice && (
                    <div className={`font-['DM_Sans'] text-sm mb-1 ${
                      plan.premium ? 'text-[#888680]' : 'text-[#888888]'
                    }`}>
                      <span className="line-through">₹{plan.originalPrice}</span>
                    </div>
                  )}

                  {/* Current Price */}
                  <div className="flex items-baseline">
                    <span className={`font-['Bebas_Neue'] text-5xl ${
                      plan.premium ? 'text-[#e8ff47]' : 'text-[#0d0d0d]'
                    }`}>
                      ₹{plan.price}
                    </span>
                    <span className={`font-['DM_Sans'] text-sm ml-2 ${
                      plan.premium ? 'text-[#888680]' : 'text-[#888888]'
                    }`}>
                      /{plan.sessions || 'session'}
                    </span>
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.includes.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          plan.premium ? 'text-[#e8ff47]' : 'text-[#0d0d0d]'
                        }`} />
                        <span className={`font-['DM_Sans'] text-sm ${
                          plan.premium ? 'text-[#dfddd6]' : 'text-[#302f2c]'
                        }`}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <QuickPayButton 
                    packageType={plan.id === 1 ? 'starter' : plan.id === 2 ? 'pro' : 'transformation'}
                    packageName={plan.name}
                    price={`₹${plan.price}`}
                  />

                  <p className={`font-['DM_Sans'] text-xs text-center mt-3 ${
                    plan.premium ? 'text-[#e8ff47]' : 'text-[#888888]'
                  }`}>
                    Or{' '}
                    <button
                      onClick={scrollToBooking}
                      className={`underline transition-colors ${
                        plan.premium ? 'text-[#e8ff47] hover:opacity-80' : 'hover:text-[#e8ff47]'
                      }`}
                    >
                      book now, pay later
                    </button>
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Gym Plans - Contact Based */}
        {activeTab === 'gyms' && (
          <div className="max-w-2xl mx-auto text-center">
            <Card className="bg-[#0d0d0d] border-[#e8ff47] border-2">
              <CardContent className="py-16 px-8">
                <h3 className="font-['Bebas_Neue'] text-[#e8ff47] text-4xl mb-6">
                  Custom Gym Partnership Packages
                </h3>
                <p className="font-['DM_Sans'] text-[#dfddd6] text-lg leading-relaxed mb-8">
                  FIT-FRAME helps your gym become a brand that people talk about, follow, and walk into.
                  <br /><br />
                  Let's build something your gym is proud of.
                </p>
                <a
                  href="https://drive.google.com/file/d/18muzAIkrNvsmzHNxgkgFzGZaZdC2-d6g/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    className="bg-[#e8ff47] text-[#0d0d0d] hover:opacity-90 hover:scale-105 rounded-full px-10 py-6 font-['DM_Sans'] font-semibold text-base uppercase transition-all"
                  >
                    Gym Pricing PDF
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Gym Contact Modal */}
      <GymContactModal isOpen={isGymModalOpen} onClose={() => setIsGymModalOpen(false)} />
    </section>
  );
};

export default Pricing;
